import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useState } from 'react';
import { Button, Checkbox, FormControlLabel, MenuItem, TextField, Typography } from '@mui/material';
import { SimpleModal, toast } from '@livable/saas-react-components';
import { observer } from 'mobx-react-lite';
import { useStore } from 'src/stores';
import useStyles from './attachBillModalStyles';
const DASH = '—';

/**
 * Approve-on-attach (LB-5766): attach a Rejected or Needs-property bill to a property, and in the
 * same gesture teach the system what made it attachable — allow the sender (or their whole domain)
 * and save the account number — so the next bill from this sender never needs a person.
 *
 * The checkboxes only appear when they would do something: a bill with no sender offers no sender
 * line, a bill with no extracted account number offers no account line. Defaults are deliberately
 * "yes, save these rules" (PRD §6.6) so the system gets smarter with use — the escape hatch is the
 * secondary "attach without saving rules" action, not unticking three boxes.
 */
export const AttachBillModal = observer(_ref => {
  let row = _ref.row,
    isOpen = _ref.isOpen,
    handleClose = _ref.handleClose,
    onAttached = _ref.onAttached;
  const store = useStore();
  const classes = useStyles();
  const sender = (row === null || row === void 0 ? void 0 : row.senderEmail) || '';
  const domain = sender.includes('@') ? sender.split('@').pop() : '';
  // The dashboard row carries the RAW extracted number (the mapped table row holds a display dash).
  const accountNumber = row !== null && row !== void 0 && row.accountNumber && row.accountNumber !== DASH ? row.accountNumber : '';
  const _useState = useState({
      propertyId: '',
      serviceType: '',
      allowSender: true,
      allowDomain: false,
      saveAccount: true,
      isSaving: false
    }),
    _useState2 = _slicedToArray(_useState, 2),
    state = _useState2[0],
    setState = _useState2[1];
  const properties = store.company.propertiesList || [];
  const selectedProperty = properties.find(property => property.propertyId === state.propertyId);
  // Unique service labels of the chosen property, for scoping the saved account number. Optional:
  // blank means the number covers every service the provider bills for.
  const serviceTypes = [...new Set(((selectedProperty === null || selectedProperty === void 0 ? void 0 : selectedProperty.utilities) || []).map(utility => utility.utilityType).filter(Boolean))];
  const set = patch => setState(previous => _objectSpread(_objectSpread({}, previous), patch));
  const handleAttach = async saveRules => {
    var _resp$response;
    // The row comes from the live list and can vanish under the modal (refresh, delete elsewhere).
    if (!row) return;
    set({
      isSaving: true
    });
    const resp = await store.companyBills.attachBillAsync.run({
      billId: row.billId,
      propertyId: state.propertyId,
      addAllowedSender: saveRules && !!sender && state.allowSender,
      addAllowedSenderDomain: saveRules && !!domain && state.allowDomain,
      saveUtilityAccount: saveRules && !!accountNumber && state.saveAccount,
      serviceType: state.serviceType || null
    });
    set({
      isSaving: false
    });
    if (!resp || resp.isSuccessful === false) {
      // Network errors are toasted by the async layer; a 409 answer (wrong status, concurrent
      // change) arrives here with the service's message, which is the actionable part.
      if (resp !== null && resp !== void 0 && resp.message) toast('error', {
        body: [resp.message]
      });
      return;
    }
    const notes = ((_resp$response = resp.response) === null || _resp$response === void 0 ? void 0 : _resp$response.notes) || [];
    toast('success', {
      body: [saveRules ? 'Bill attached — rules saved.' : 'Bill attached.', ...notes]
    });
    onAttached === null || onAttached === void 0 || onAttached();
  };
  const canAttach = !!row && !!state.propertyId && !state.isSaving;
  return /*#__PURE__*/React.createElement(SimpleModal, {
    isOpen: isOpen,
    handleClose: handleClose,
    title: "Attach to property",
    subTitle: "The bill re-runs against the property you choose. Saved rules make the next bill attach itself."
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.content
  }, /*#__PURE__*/React.createElement(TextField, {
    select: true,
    label: "Property",
    value: state.propertyId,
    onChange: event => set({
      propertyId: event.target.value,
      serviceType: ''
    }),
    disabled: state.isSaving,
    required: true
  }, properties.map(property => /*#__PURE__*/React.createElement(MenuItem, {
    key: property.propertyId,
    value: property.propertyId
  }, property.propertyAddress || property.propertyName || property.propertyId))), !!sender && /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(FormControlLabel, {
    control: /*#__PURE__*/React.createElement(Checkbox, {
      checked: state.allowSender,
      onChange: event => set({
        allowSender: event.target.checked
      }),
      disabled: state.isSaving
    }),
    label: "Add ".concat(sender, " as an allowed sender")
  }), !!domain && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(FormControlLabel, {
    control: /*#__PURE__*/React.createElement(Checkbox, {
      checked: state.allowDomain,
      onChange: event => set({
        allowDomain: event.target.checked
      }),
      disabled: state.isSaving
    }),
    label: "Allow the entire @".concat(domain, " domain")
  }), /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    color: "light",
    className: classes.hint
  }, "Use the domain when the provider sends from changing addresses (billing@, no-reply-7@, \u2026)."))), !!accountNumber && /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(FormControlLabel, {
    control: /*#__PURE__*/React.createElement(Checkbox, {
      checked: state.saveAccount,
      onChange: event => set({
        saveAccount: event.target.checked
      }),
      disabled: state.isSaving
    }),
    label: "Save account #".concat(accountNumber, " for this property")
  }), state.saveAccount && /*#__PURE__*/React.createElement(TextField, {
    select: true,
    label: "For service (optional)",
    value: state.serviceType,
    onChange: event => set({
      serviceType: event.target.value
    }),
    helperText: "Leave blank if this number covers every service.",
    disabled: state.isSaving || !state.propertyId,
    className: classes.serviceField
  }, /*#__PURE__*/React.createElement(MenuItem, {
    value: ""
  }, "Every service"), serviceTypes.map(serviceType => /*#__PURE__*/React.createElement(MenuItem, {
    key: serviceType,
    value: serviceType
  }, serviceType)))), /*#__PURE__*/React.createElement("div", {
    className: classes.actions
  }, /*#__PURE__*/React.createElement(Button, {
    variant: "contained",
    disabled: !canAttach,
    onClick: () => handleAttach(true)
  }, state.isSaving ? 'Attaching…' : 'Attach & save rules'), /*#__PURE__*/React.createElement(Button, {
    variant: "actionButton",
    disabled: !canAttach,
    onClick: () => handleAttach(false)
  }, "Attach without saving rules"), /*#__PURE__*/React.createElement(Button, {
    variant: "actionButtonInvert",
    disabled: state.isSaving,
    onClick: handleClose
  }, "Cancel"))));
});